'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, type ReactNode } from 'react';
import {
  CartProvider,
  ModalProvider,
  ApplicationErrorComponent,
} from 'components';
import { UserAnalyticsProvider } from 'components/shared/UserAnalytics';
import { ToastProvider } from 'components/shared';
import { graphik, graphikItalic, graphikExtended } from 'utils/fonts';
import { TABLET_MAX_WIDTH_IN_PX } from 'components/theme';
import { MagicModalNewProvider } from 'components/shared/MagicModalNew';
import { SkipLink } from 'components/shared/SkipLink';
import { CookieConsentBanner } from 'components/shared/CookieConsentBanner';
import { ConsentRuntimeProvider } from 'components/shared/consent/ConsentRuntime';
import { PerformanceLabGoogleAdsHead } from 'components/performance-lab/PerformanceLabGoogleAdsHead';
import { GlobalUIProvider } from 'contexts/GlobalUIContext';
import { RouteIntlProvider } from 'lib/i18n/routeIntl';
import { ErrorBoundary } from 'next/dist/client/components/error-boundary';
import type { Campaign, SddDeliveryBanner } from 'content/campaigns';
import {
  RegionLocaleContext,
  RegionLocaleContextType,
} from 'contexts/RegionLocaleContext';

const DeferredVercelAnalytics = dynamic(
  () =>
    import('components/shared/DeferredVercelAnalytics').then((mod) => ({
      default: mod.DeferredVercelAnalytics,
    })),
  { ssr: false },
);

/**
 * Client provider tree for the App Router — mirrors pages/_app.tsx exactly so
 * migrated routes render inside the same shell (Header, Footer, Cart, …).
 */
export function Providers({
  children,
  regionLocale,
  initialCampaigns,
  initialSddDelivery,
}: {
  children: ReactNode;
  regionLocale: RegionLocaleContextType;
  initialCampaigns?: Campaign[];
  initialSddDelivery?: SddDeliveryBanner | null;
}) {
  const setWindowHeightAsCssVar = useCallback((firstCall: boolean) => {
    // do not resize elements on mobiles
    // because of weird behavious when side bars show/disappear
    if (window.innerWidth > TABLET_MAX_WIDTH_IN_PX || firstCall) {
      document.documentElement.style.setProperty(
        '--viewportHeight',
        `${window.innerHeight}px`,
      );
    }
  }, []);

  useEffect(() => {
    setWindowHeightAsCssVar(true);

    let resizeRaf = 0;
    const resizeListener = () => {
      if (resizeRaf) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = 0;
        setWindowHeightAsCssVar(false);
      });
    };
    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
    };
  }, []);

  return (
    <RegionLocaleContext.Provider value={regionLocale}>
      {/* graphik.className / graphikItalic.variable / graphikExtended.variable are the css anchors — without a client-side usage Turbopack drops the @font-face */}
      <div
        className={`${graphik.className} ${graphikItalic.variable} ${graphikExtended.variable}`}
      >
        <ConsentRuntimeProvider>
          <PerformanceLabGoogleAdsHead />
          <div id="main-content">
              <ErrorBoundary
                errorComponent={() => <ApplicationErrorComponent />}
              >
                <GlobalUIProvider>
                  <SkipLink />
                  <CookieConsentBanner />
                  <RouteIntlProvider>
                    <ToastProvider>
                      <UserAnalyticsProvider>
                        <CartProvider
                          initialCampaigns={initialCampaigns}
                          initialSddDelivery={initialSddDelivery}
                        >
                          <ModalProvider>
                            <MagicModalNewProvider>
                              {children}
                            </MagicModalNewProvider>
                          </ModalProvider>
                        </CartProvider>
                      </UserAnalyticsProvider>
                    </ToastProvider>
                  </RouteIntlProvider>
                </GlobalUIProvider>
              </ErrorBoundary>
            </div>
            <DeferredVercelAnalytics />
        </ConsentRuntimeProvider>
          </div>
    </RegionLocaleContext.Provider>
  );
}
